import React, { useEffect } from 'react';
import { CAProvider } from '../../contexts/CAContext';
import { MiawChatProvider } from '../../contexts/MiawChatContext';
import Chat from './Chat';
import './Chat.styles.scss';
import { IChatSession, IIdentityObject, IPageInfo } from '../../interfaces/app-interfaces';
import { ChatbotProvider } from '../../contexts/ChatbotContext';
import { clearChatbotToken, getChatSessionType, getChatWindowState, getIsUserLoggedInAtChatStart, getUnreadCount } from '../../utils/session-storage-utils';
import { CaseDataProvider } from '../../contexts/CaseDataContext';
import { getPageInfo } from '../../utils/app-utils';
import { CHAT_BUTTON_TEXT, CHAT_HEADER_TEXT } from '../../config/app-config';
import { initTranslations } from '../../utils/translations';
import { initChatConfig } from '../../utils/miaw/init-chat-utils';
import { ISalesforceConfig } from '../../interfaces/chat-config-interfaces';
import useSwanCustomStyles from '../../hooks/custom-hooks/useSwanCustomStyles';
import { useNRLogging } from '../../hooks/custom-hooks/useNRLogging';
import { isStudioPage } from '../../utils/chat/page-utils';
import { useIdentity } from '@vp/auth-react';
import { isSessionOlderThan24Hours } from '../../utils/init-utils/init-chatbot-utils';

interface IChatbotProps {
  enableChatbot?: boolean;
  onChatButtonClick?: () => void;
  locale: string;
  tenant?: string;
}

const Chatbot: React.FC<IChatbotProps> = ({ onChatButtonClick, locale, enableChatbot = true, tenant }) => {
  const { logCustomAttributesNR } = useNRLogging();
  const [chatConfig, setChatConfig] = React.useState<ISalesforceConfig>();
  const [isTranslationsInitialized, setIsTranslationsInitialized] = React.useState(false);
  const [chatSession, setChatSession] = React.useState<IChatSession>();
  const [chatWindowState, setChatWindowState] = React.useState<'minimized' | 'maximized' | 'hidden'>('minimized');
  const [pageInfo, setPageInfo] = React.useState<IPageInfo>();
  const identity = useIdentity();

  /** To check the type of session which is active */
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Initialize chat configuration based on the locale
    initChatConfig(locale)
      .then((data) => {
        console.log('Chat config initialized:', data);
        setChatConfig(data);
      })
      .catch((error) => {
        console.error('Failed to initialize chat config:', error);
        logCustomAttributesNR('ca-chatbot-init-error', { logLevel: 'error', message: error.message, errorType: 'chatConfigError' });
      });

    // Load translations when component mounts
    initTranslations(locale)
      .then(() => {
        setIsTranslationsInitialized(true);
      })
      .catch((error) => {
        console.error('Failed to load translations:', error);
        logCustomAttributesNR('ca-chatbot-init-error', { logLevel: 'error', message: error.message, errorType: 'translationsError' });
      });

    // Get page info for tracking
    const { pageName, pageSection, pageStage } = getPageInfo();
    setPageInfo({ pageName, pageSection, pageStage });

    // Get chat window state from session storage
    const chatWindowStateFromStorage = getChatWindowState();
    if (chatWindowStateFromStorage === 'maximized') {
      setChatWindowState('maximized');
    } else {
      setChatWindowState('minimized');
    }

    // Get chat session type from session storage
    const chatSesssionFromStorage = getChatSessionType();
    const { isSignedIn } = identity as IIdentityObject;

    /** if user is currently not signed in and was signed in when chat started, reset the chat session */
    if (isSignedIn === false && getIsUserLoggedInAtChatStart() === 'true') {
      setChatSession('initial');
    } else if (chatSesssionFromStorage === 'miawChatSession' || chatSesssionFromStorage === 'chatbotSession') {
      if (isSessionOlderThan24Hours()) {
        setChatSession('initial');
        clearChatbotToken();
      }
      setChatSession(chatSesssionFromStorage);
    } else {
      setChatSession('initial');
    }

    if (isStudioPage()) {
      if (chatSesssionFromStorage === 'chatbotSession' || chatSesssionFromStorage === 'miawChatSession') {
        // Do nothing
      } else {
        setChatWindowState('hidden');
      }
    }
  }, []);

  /** Render when session type is determined */
  if (!chatSession || !isTranslationsInitialized || !chatConfig) {
    return null;
  }
  return (
    <CAProvider
      tenant={tenant}
      locale={locale}
      chatWindow={chatWindowState}
      chatSession={chatSession}
      enableChatbot={enableChatbot}
      isPrivacyPolicyAccepted={chatSession !== 'initial'}
      chatButtonText={enableChatbot ? CHAT_BUTTON_TEXT.CHATBOT : CHAT_BUTTON_TEXT.MIAW_CHAT}
      chatHeaderText={enableChatbot ? CHAT_HEADER_TEXT.CHATBOT : CHAT_HEADER_TEXT.MIAW_CHAT}
      chatButtonHandler={() => {}}
      chatWindowHandler={() => {}}
      unreadMessages={getUnreadCount() || 0}
      enableBHCheckInEmode={chatConfig?.enableBHCheckInEmode || false}
      enableIsCustomerCheck={chatConfig?.enableIsCustomerCheck || false}
    >
      <CaseDataProvider data={{ pageInfo, queueType: 'default' }}>
        <ChatbotProvider conversationMessages={[]} latestQuery={''}>
          <MiawChatProvider
            conversationMessages={[]}
            sfConfig={{
              orgId: chatConfig?.orgId,
              developerName: chatConfig?.developerName,
              scrtURL: chatConfig?.scrtURL,
            }}
            queueInfo={chatConfig?.queueInfo}
            enableTypingIndicator={chatConfig?.enableTypingIndicator || false}
            enableSneakPeak={chatConfig?.enableSneakPeak || false}
          >
            <Chat onChatButtonClick={onChatButtonClick} />
          </MiawChatProvider>
        </ChatbotProvider>
      </CaseDataProvider>
    </CAProvider>
  );
};

export const ChatbotWrapper: React.FC<IChatbotProps> = (props) => {
  const { isLoading } = useSwanCustomStyles(props.locale, props.tenant || 'vistaprint');
  if (isLoading) return <></>;
  return <Chatbot {...props} />;
};

export default ChatbotWrapper;
